/**
 * SignalR service for real-time orchestration events.
 * Provides connection management, automatic reconnection, and type-safe event handlers.
 * 
 * This is the new real-time transport replacing SSE for improved scalability
 * and bidirectional communication support.
 */
import * as signalR from '@microsoft/signalr'
import { apiOrigin, getConfigOptional } from './config'
import { CSRF_HEADER_NAME, XSRF_COOKIE_NAME, readCookie } from './bff'

// ============================================================================
// Types
// ============================================================================

/**
 * Connection state for the SignalR connection
 */
export type SignalRConnectionState = 
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'disconnecting'

/**
 * Event types that can be received from the OrchestrationHub.
 * These match the server-side OrchestrationHubEvents constants.
 */
export type OrchestrationHubEvent =
  // Session lifecycle events
  | 'SessionStatusChanged'
  | 'ProgressUpdate'
  | 'ToolCalled'
  | 'ClarificationNeeded'
  | 'SessionComplete'
  | 'SessionError'
  // Agent / tool lifecycle events
  | 'AgentStarted'
  | 'ToolCompleted'
  | 'PhaseChanged'
  // Chat events
  | 'ChatMessageStart'
  | 'ChatToken'
  | 'ChatToolCall'
  | 'ChatToolResult'
  | 'ChatComplete'
  | 'ChatError'
  | 'ChatEscalationStart'
  | 'ChatReasoning'
  // Supervisor events
  | 'PolicyDecision'
  | 'RetryAttempt'
  | 'GuardianIteration'
  | 'SubTaskCompleted'
  // Context observability events
  | 'ChatContextSnapshot'

/**
 * Event payload types for type-safe event handling
 */
export interface SessionStatusChangedPayload {
  sessionId: string
  status: string
  previousStatus?: string
  timestamp: string
}

export interface ProgressUpdatePayload {
  sessionId: string
  message: string
  progress?: number
  timestamp: string
}

export interface ToolCalledPayload {
  sessionId: string
  toolName: string
  toolInput?: unknown
  result?: unknown
  timestamp: string
}

export interface ClarificationNeededPayload {
  sessionId: string
  question: string
  options?: string[]
  timestamp: string
}

export interface SessionCompletePayload {
  sessionId: string
  response: string
  timestamp: string
}

export interface SessionErrorPayload {
  sessionId: string
  error: string
  errorCode?: string
  timestamp: string
}

export interface ChatMessageStartPayload {
  sessionId: string
  seq: number  // Sequence number for ordering (the producer assigns this turn's first seq, normally 1)
  timestamp: string
}

export interface ChatTokenPayload {
  sessionId: string
  token: string
  seq: number  // Sequence number for ordering
  timestamp: string
}

export interface ChatToolCallPayload {
  sessionId: string
  toolName: string
  toolInput: unknown
  toolCallId?: string
  seq: number  // Sequence number for ordering
  timestamp: string
}

export interface ChatToolResultPayload {
  sessionId: string
  toolCallId?: string  // Use this to match with tool calls
  result: unknown
  seq: number  // Sequence number for ordering
  timestamp: string
}

export interface ChatCompletePayload {
  sessionId: string
  response: string
  seq: number  // Sequence number for ordering
  timestamp: string
}

export interface ChatErrorPayload {
  sessionId: string
  error: string
  seq: number  // Sequence number for ordering
  timestamp: string
}

export interface ChatEscalationStartPayload {
  sessionId: string
  modelName: string
  seq: number  // Sequence number for ordering
  timestamp: string
}

export interface ChatReasoningPayload {
  sessionId: string
  reasoning: string
  seq: number  // Always 0 — reasoning deltas are out-of-band and do not participate in the ordered token stream
  timestamp: string
}

export interface ChatContextSnapshotPayload {
  sessionId: string
  snapshotId: string
  summary: string
  seq: number
  timestamp: string
}

export interface PolicyDecisionPayload {
  sessionId: string
  policy: string
  decision: string
  reason?: string
  timestamp: string
}

export interface RetryAttemptPayload {
  sessionId: string
  attempt: number
  maxAttempts: number
  reason: string
  timestamp: string
}

export interface GuardianIterationPayload {
  sessionId: string
  iteration: number
  maxIterations: number
  timestamp: string
}

export interface SubTaskCompletedPayload {
  sessionId: string
  taskId: string
  taskName: string
  success: boolean
  timestamp: string
}

/**
 * Map of event names to their payload types
 */
export interface OrchestrationEventMap {
  SessionStatusChanged: SessionStatusChangedPayload
  ProgressUpdate: ProgressUpdatePayload
  ToolCalled: ToolCalledPayload
  ClarificationNeeded: ClarificationNeededPayload
  SessionComplete: SessionCompletePayload
  SessionError: SessionErrorPayload
  ChatMessageStart: ChatMessageStartPayload
  ChatToken: ChatTokenPayload
  ChatToolCall: ChatToolCallPayload
  ChatToolResult: ChatToolResultPayload
  ChatComplete: ChatCompletePayload
  ChatError: ChatErrorPayload
  ChatEscalationStart: ChatEscalationStartPayload
  ChatReasoning: ChatReasoningPayload
  PolicyDecision: PolicyDecisionPayload
  RetryAttempt: RetryAttemptPayload
  GuardianIteration: GuardianIterationPayload
  SubTaskCompleted: SubTaskCompletedPayload
  ChatContextSnapshot: ChatContextSnapshotPayload
  AgentStarted: Record<string, unknown>
  ToolCompleted: Record<string, unknown>
  PhaseChanged: Record<string, unknown>
}

/**
 * Callback type for event handlers
 */
export type EventCallback<T extends OrchestrationHubEvent> = (
  payload: OrchestrationEventMap[T]
) => void

/**
 * Options for SignalR service initialization
 */
export interface SignalRServiceOptions {
  /** Override the default hub URL */
  hubUrl?: string
  /** Enable automatic reconnection (default: true) */
  autoReconnect?: boolean
  /** Reconnection delay intervals in milliseconds */
  reconnectDelays?: number[]
  /** Log level for SignalR client */
  logLevel?: signalR.LogLevel
  /** Callback when connection state changes */
  onStateChange?: (state: SignalRConnectionState) => void
  /** Callback when reconnecting */
  onReconnecting?: (error?: Error) => void
  /** Callback when reconnected */
  onReconnected?: (connectionId?: string) => void
  /** Callback when connection is closed */
  onClose?: (error?: Error) => void
}

// ============================================================================
// SignalR Service Class
// ============================================================================

/**
 * The antiforgery header for the hub's own requests, read at the moment each one is sent.
 *
 * A getter rather than a snapshot because SignalR keeps this object for the life of the connection and
 * spreads it into every request — including the negotiate of an automatic reconnect, which may happen
 * long after the connection was built. The token is stable across refresh rotation today
 * (`RefreshRotationMiddleware` carries it over deliberately), so this is belt and braces; it costs one
 * cookie read per request and removes the question entirely.
 */
function csrfHeaders(): Record<string, string> {
  return {
    get [CSRF_HEADER_NAME](): string {
      return readCookie(XSRF_COOKIE_NAME) ?? ''
    },
  } as Record<string, string>
}

/**
 * SignalR service for managing real-time orchestration connections.
 * 
 * @example
 * ```ts
 * const service = new SignalRService()
 * await service.start()
 * 
 * // Subscribe to session events
 * await service.subscribe('session-123')
 * 
 * // Register event handlers
 * service.on('ChatToken', (payload) => {
 *   console.log('Token:', payload.token)
 * })
 * 
 * service.on('SessionComplete', (payload) => {
 *   console.log('Complete:', payload.response)
 * })
 * 
 * // Cleanup when done
 * await service.unsubscribe('session-123')
 * await service.stop()
 * ```
 */
export class SignalRService {
  private connection: signalR.HubConnection | null = null
  private options: SignalRServiceOptions
  private eventHandlers: Map<string, Set<EventCallback<OrchestrationHubEvent>>> = new Map()
  private subscribedSessions: Set<string> = new Set()

  /**
   * The connection attempt in flight, so a second caller joins it instead of racing past it.
   * Null whenever there is nothing to join — before the first attempt and after any settles.
   */
  private startPromise: Promise<void> | null = null
  private _connectionState: SignalRConnectionState = 'disconnected'

  constructor(options: SignalRServiceOptions = {}) {
    this.options = {
      autoReconnect: true,
      reconnectDelays: [0, 2000, 5000, 10000, 30000],
      logLevel: signalR.LogLevel.Information,
      ...options
    }
  }

  /**
   * Get the current connection state
   */
  get connectionState(): SignalRConnectionState {
    return this._connectionState
  }

  /**
   * Get the hub URL from config or options
   */
  private getHubUrl(): string {
    if (this.options.hubUrl) {
      return this.options.hubUrl
    }
    return `${apiOrigin()}/hubs/orchestration`
  }

  /**
   * Build the SignalR connection with proper configuration
   */
  private buildConnection(): signalR.HubConnection {
    const hubUrl = this.getHubUrl()
    
    // `withCredentials` rather than an access-token factory: the hub is same-origin (D5) and the session
    // is a cookie the browser attaches by itself. This is the path most likely to be forgotten in a move
    // like this one, and it fails differently from a plain fetch — the negotiate answers 401 and the
    // socket never opens, so the UI simply stops receiving live updates rather than showing an error.
    //
    // The antiforgery token comes with it, because `negotiate` is a POST and cookie authentication makes
    // every state-changing request forgeable by any page that can reach this origin. A hub is not exempt
    // from that: an attacker's page cannot read the response, but it could open a subscription. The
    // WebSocket upgrade itself is a GET and needs nothing; negotiate and the long-polling fallback do.
    let builder = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, { withCredentials: true, headers: csrfHeaders() })
      .configureLogging(this.options.logLevel!)

    // Configure automatic reconnection
    if (this.options.autoReconnect) {
      builder = builder.withAutomaticReconnect(this.options.reconnectDelays!)
    }

    return builder.build()
  }

  /**
   * Set connection state and notify listeners
   */
  private setConnectionState(state: SignalRConnectionState): void {
    this._connectionState = state
    this.options.onStateChange?.(state)
  }

  /**
   * Setup connection event handlers
   */
  private setupConnectionHandlers(): void {
    if (!this.connection) return

    this.connection.onreconnecting((error) => {
      this.setConnectionState('reconnecting')
      this.options.onReconnecting?.(error)
    })

    this.connection.onreconnected((connectionId) => {
      this.setConnectionState('connected')
      this.options.onReconnected?.(connectionId)
      
      // Re-subscribe to all sessions after reconnection
      this.resubscribeAll()
    })

    this.connection.onclose((error) => {
      this.setConnectionState('disconnected')
      this.options.onClose?.(error)
    })
  }

  /**
   * Re-subscribe to all sessions after reconnection
   */
  private async resubscribeAll(): Promise<void> {
    for (const sessionId of this.subscribedSessions) {
      try {
        await this.connection?.invoke('Subscribe', sessionId)
      } catch (error) {
        console.error(`Failed to resubscribe to session ${sessionId}:`, error)
      }
    }
  }

  /**
   * Register handlers for all known event types
   */
  private registerServerEventHandlers(): void {
    if (!this.connection) return

    const eventTypes: OrchestrationHubEvent[] = [
      'SessionStatusChanged',
      'ProgressUpdate',
      'ToolCalled',
      'ClarificationNeeded',
      'SessionComplete',
      'SessionError',
      'AgentStarted',
      'ToolCompleted',
      'PhaseChanged',
      'ChatMessageStart',
      'ChatToken',
      'ChatToolCall',
      'ChatToolResult',
      'ChatComplete',
      'ChatError',
      'ChatEscalationStart',
      'ChatReasoning',
      'PolicyDecision',
      'RetryAttempt',
      'GuardianIteration',
      'SubTaskCompleted',
      'ChatContextSnapshot'
    ]

    for (const eventType of eventTypes) {
      this.connection.on(eventType, (payload: unknown) => {
        const handlers = this.eventHandlers.get(eventType)
        if (handlers) {
          for (const handler of handlers) {
            try {
              handler(payload as OrchestrationEventMap[typeof eventType])
            } catch (error) {
              console.error(`Error in ${eventType} handler:`, error)
            }
          }
        }
      })
    }
  }

  /**
   * Start the SignalR connection
   */
  async start(): Promise<void> {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      return
    }

    // A second caller arriving mid-connect joins the attempt rather than returning past it.
    //
    // This branch used to `return` on `Connecting` under a comment that said "wait for existing
    // connection attempt" — which it did not do. `start()` resolved while the socket was still opening,
    // the caller took that as connected and invoked `Subscribe`, and `subscribe()` threw because the
    // state was not `Connected` yet. The throw was caught and turned into an error string, and nothing
    // ever retried.
    //
    // The result was the quietest possible failure: the WebSocket opens, the handshake completes, and
    // the connection sits there exchanging pings forever with no group joined. No events arrive, nothing
    // reports a problem, and the answer only appears once the page is reloaded and reads it from history.
    // Read off a HAR on 2026-09-19, where the socket's only frames for a whole minute were `{"type":6}`.
    if (this.startPromise) {
      return this.startPromise
    }

    this.startPromise = this.openConnection().finally(() => {
      this.startPromise = null
    })

    return this.startPromise
  }

  /** The connection attempt itself. Callers go through {@link start}, which makes it single-flight. */
  private async openConnection(): Promise<void> {
    this.setConnectionState('connecting')

    try {
      this.connection = this.buildConnection()
      this.setupConnectionHandlers()
      this.registerServerEventHandlers()

      await this.connection.start()
      this.setConnectionState('connected')
    } catch (error) {
      this.setConnectionState('disconnected')
      throw error
    }
  }

  /**
   * Stop the SignalR connection
   */
  async stop(): Promise<void> {
    if (!this.connection) return

    this.setConnectionState('disconnecting')
    this.subscribedSessions.clear()

    try {
      await this.connection.stop()
    } finally {
      this.setConnectionState('disconnected')
      this.connection = null
    }
  }

  /**
   * Subscribe to session events
   * @param sessionId The session ID to subscribe to
   */
  async subscribe(sessionId: string): Promise<void> {
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      throw new Error('SignalR connection not established')
    }

    await this.connection.invoke('Subscribe', sessionId)
    this.subscribedSessions.add(sessionId)
  }

  /**
   * Unsubscribe from session events
   * @param sessionId The session ID to unsubscribe from
   */
  async unsubscribe(sessionId: string): Promise<void> {
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      return
    }

    await this.connection.invoke('Unsubscribe', sessionId)
    this.subscribedSessions.delete(sessionId)
  }

  /**
   * Register an event handler
   * @param event The event type to handle
   * @param callback The callback function
   * @returns A function to unregister the handler
   */
  on<T extends OrchestrationHubEvent>(
    event: T,
    callback: EventCallback<T>
  ): () => void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set())
    }
    
    const handlers = this.eventHandlers.get(event)!
    handlers.add(callback as EventCallback<OrchestrationHubEvent>)

    // Return unsubscribe function
    return () => {
      handlers.delete(callback as EventCallback<OrchestrationHubEvent>)
    }
  }

  /**
   * Remove all handlers for an event type
   * @param event The event type
   */
  off(event: OrchestrationHubEvent): void {
    this.eventHandlers.delete(event)
  }

  /**
   * Remove all event handlers
   */
  offAll(): void {
    this.eventHandlers.clear()
  }
}

// ============================================================================
// Singleton Instance
// ============================================================================

let signalRInstance: SignalRService | null = null

/**
 * Get or create the singleton SignalR service instance
 */
export function getSignalRService(options?: SignalRServiceOptions): SignalRService {
  if (!signalRInstance) {
    signalRInstance = new SignalRService(options)
  }
  return signalRInstance
}

/**
 * Dispose the singleton instance
 */
export async function disposeSignalRService(): Promise<void> {
  if (signalRInstance) {
    await signalRInstance.stop()
    signalRInstance = null
  }
}

// ============================================================================
// Feature Flag
// ============================================================================

/**
 * Whether live updates are turned on for this deployment. Off unless `VITE_USE_SIGNALR` says otherwise.
 *
 * <p>
 * Read with {@link getConfigOptional} because the setting is optional — `RuntimeConfig` has always
 * declared it so. {@link getConfig} THROWS on a key it cannot find, so an installation that simply had no
 * opinion about live updates did not get them turned off, it got an exception thrown out of the first
 * component to ask. The chart renders the key with a default and so hides this; a `.env` without it, or
 * any non-Helm way of serving the bundle, does not.
 * </p>
 */
export function isSignalREnabled(): boolean {
  return getConfigOptional('VITE_USE_SIGNALR')?.toLowerCase() === 'true'
}
